<template>
  <div class="t-app__container">
    <heading :overTitle="$t('superadmin-admin-dashboard-overTitle')" :title="$t('superadmin-admin-dashboard-title')" :text="$t('superadmin-admin-dashboard-text')" />
    <div class="h-section h-section--small-top">
      <admin-dashboard-tabs />
      <dashboard-section :title="$t('dashboard-admin-superadmin-title')">
        <!-- unsubmit gatherings -->
        <div class="super-admin-action">
          <button class="a-btn-primary" @click="updateUnsubmitGatheringsPanel(true)">{{ $t('unsubmit-title-button') }}</button>
          <div class="o-panel" v-bind:class="{ 'o-panel--open': isUnsubmitGatheringsPanelOpen }">
            <div class="o-panel__head">
              <h2 class="o-panel__title">{{ $t('unsubmit-gatherings-title') }}</h2>
              <button type="button" class="o-panel__close" @click="updateUnsubmitGatheringsPanel(false)">{{ $t('panel-close') }}</button>
            </div>
            <div class="o-panel__content">
              <strong-confirmation-popup :confirmationMessage="$t('unsubmit-gatherings-userinput')" :confirmButton="unsubmitGatheringsButtons.confirm" :cancelButton="unsubmitGatheringsButtons.cancel">
                <p>{{ $t('unsubmit-gathering-confirmation-text').replace('{user-input}', $t('unsubmit-gatherings-userinput')) }}</p>
              </strong-confirmation-popup>
            </div>
          </div>
        </div>

        <!-- deactivate businesses -->
        <div class="super-admin-action">
          <button class="a-btn-primary" @click="updateDeactivateBusinessesPanel(true)">{{ $t('deactivate-businesses-title-button') }}</button>
          <div class="o-panel" v-bind:class="{ 'o-panel--open': isDeactivateBusinessesPanelOpen }">
            <div class="o-panel__head">
              <h2 class="o-panel__title">{{ $t('deactivate-businesses-title') }}</h2>
              <button type="button" class="o-panel__close" @click="updateDeactivateBusinessesPanel(false)">{{ $t('panel-close') }}</button>
            </div>
            <div class="o-panel__content">
              <strong-confirmation-popup
                :confirmationMessage="$t('deactivate-businesses-userinput')"
                :confirmButton="deactivateBusinessesButtons.confirm"
                :cancelButton="deactivateBusinessesButtons.cancel"
              >
                <div>{{ $t('deactivate-businesses-confirmation-title') }}</div>
                <ul>
                  <li>{{ $t('deactivate-businesses-confirmation-list1') }}</li>
                  <li>{{ $t('deactivate-businesses-confirmation-list2') }}</li>
                  <li>{{ $t('deactivate-businesses-confirmation-list3') }}</li>
                </ul>
                <div>{{ $t('deactivate-businesses-confirmation-text').replace('{user-input}', $t('deactivate-businesses-userinput')) }}</div>
              </strong-confirmation-popup>
            </div>
          </div>
        </div>

        <!-- activate user -->
        <br />
        <div class="super-admin-action activate-user-section">
          <h3>{{ $t('activate-user-admin-section-title') }}</h3>
          <s-form @submit="activateUser">
            <p class="form-group">
              <label class="a-form-label" for="emailAddress" :value="email">{{ $t('activate-user-email-address') }}</label>
              <input class="a-form-control" :value="email" @change="setEmail($event.target.value)" :placeholder="$t('activate-user-email-address-placeholder')" id="emailAddress" />
              <span v-if="$v.email.$dirty">
                <span class="m-form-group__error" v-if="!$v.email.required">{{ $t('error-field-required') }}</span>
                <span class="m-form-group__error" v-if="!$v.email.email">{{ $t('error-field-not-email') }}</span>
              </span>
            </p>
            <div class="m-form-ctrl">
              <div class="m-form-ctrl__item">
                <button class="a-btn-primary" type="submit">
                  {{ $t('activate-user-title-button') }}
                </button>
              </div>
            </div>
          </s-form>
        </div>
      </dashboard-section>
    </div>
  </div>
</template>

<script>
import { required, email } from 'vuelidate/lib/validators'

import Heading from '@/components/heading'
import SForm from '@/components/form/form'
import AdminDashboardTabs from '@/components/dashboard/admin-dashboard-tabs'
import DashboardSection from '@/components/dashboard/dashboard-section'
import StrongConfirmationPopup from '@/components/popup/strong-confirmation-popup'
import i18n from '@/helpers/i18n'

import UserService from '@/services/user'
import Notification from '@/services/notification'
import LoaderService from '@/services/loader'
import GatheringService from '@/services/gathering'
import BusinessService from '@/services/business'

export default {
  components: {
    Heading,
    SForm,
    AdminDashboardTabs,
    DashboardSection,
    StrongConfirmationPopup
  },
  data() {
    return {
      isUnsubmitGatheringsPanelOpen: false,
      isDeactivateBusinessesPanelOpen: false,
      unsubmitGatheringsButtons: {
        confirm: { label: this.$t('strong-popup-confirm-label'), click: this.unsubmitAllGatherings },
        cancel: { label: this.$t('strong-popup-cancel-label'), click: () => this.updateUnsubmitGatheringsPanel(false) }
      },
      deactivateBusinessesButtons: {
        confirm: { label: this.$t('strong-popup-confirm-label'), click: this.deactivateAllBusinesses },
        cancel: { label: this.$t('strong-popup-cancel-label'), click: () => this.updateDeactivateBusinessesPanel(false) }
      },
      email: ''
    }
  },
  validations: {
    email: {
      required,
      email
    }
  },
  computed: {},
  methods: {
    async unsubmitAllGatherings() {
      await GatheringService.unsubmitAllGatherings()
      this.updateUnsubmitGatheringsPanel(false)
    },
    async deactivateAllBusinesses() {
      await BusinessService.deactivateAllBusinesses()
      this.updateDeactivateBusinessesPanel(false)
    },
    updateUnsubmitGatheringsPanel(isVisible) {
      this.isUnsubmitGatheringsPanelOpen = isVisible
    },
    updateDeactivateBusinessesPanel(isVisible) {
      this.isDeactivateBusinessesPanelOpen = isVisible
    },
    activateUser: async function() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        LoaderService.showLoader()
        try {
          const result = await UserService.manuallyActivateUser(this.email)
          if (result.ActivationSuccessful) {
            Notification.showSuccess(i18n.t('user-activated-successfully').replace('{email}', result.EmailAddress))
          } else if (result.UserAlreadyActivated) {
            Notification.showInfo(i18n.t('user-already-activated-error').replace('{email}', result.EmailAddress))
          } else if (result.UserNotFound) {
            Notification.showError(i18n.t('user-not-found-error').replace('{email}', result.EmailAddress))
          } else {
            Notification.showError(i18n.t('error-basic'))
          }
        } finally {
          LoaderService.hideLoader()
        }
      }
    },
    setEmail: function(value) {
      this.email = value
      this.$v.email.$touch()
    }
  },
  async beforeRouteEnter(to, from, next) {
    LoaderService.showLoader()

    next(vm => {
      LoaderService.hideLoader()
    })
  }
}
</script>

<style lang="scss">
.super-admin-action {
  margin-right: 20px;
  display: inline-block;
}
.activate-user-section {
  margin-top: 20px;
}
</style>
